import Vue from 'vue';
import { success, fail } from '@helpers/result-helper.js';
import toast from '@/services/toasts/index.js';

const state = {
	hrDocuments: [],
	hrDocuments_loading: false,
	hrDocumentCategories: []
};

const appendAgreementData = (x, loggedInEmployeeId, usingFullEmployeeModel = false) => {
	const agreement = usingFullEmployeeModel ? x.agreements.find(x => x.employee.id === loggedInEmployeeId) : x.agreements.find(x => x.employeeId === loggedInEmployeeId);

	let agreedData = {
		agreed: false,
		agreementDateUtc: null
	};

	if (agreement != null) {
		agreedData.agreed = true;
		agreedData.agreementDateUtc = agreement.agreementDateUtc;
	}

	return { ...x, agreedData };
};

const appendIsRelevantToEmployee = (x, loggedInEmployeeId, usingFullEmployeeModel = false) => {
	x.isRelevantToEmployee = x.relevantParties.all || (usingFullEmployeeModel ? x.relevantParties.employees.map(e => e.id).includes(loggedInEmployeeId) : x.relevantParties.employeeIds.includes(loggedInEmployeeId));
	return x;
};

const getters = {
	hrDocuments: (state, getters, rootState, rootGetters) => {
		return state.hrDocuments.map(x => {
			const loggedInEmployeeId = rootGetters['loggedInEmployee'].id;
			x = appendAgreementData(x, loggedInEmployeeId);
			x = appendIsRelevantToEmployee(x, loggedInEmployeeId);
			return x;
		});
	},
	hrPolicies: (state, getters, rootState, rootGetters) => {
		return getters.hrDocuments.filter(x => x.type === 0);
	},
	unacknowledgedPolicyCount: (state, getters) => {
		return getters.hrDocuments.filter(x => x.isRelevantToEmployee && !x.agreedData.agreed).length;
	},
	hrDocumentsWithUpdatesAvailable: (state, getters) => {
		return getters.hrDocuments.filter(x => x.newVersionAvailable);
	},
	hrDocumentCategories: state => {
		return state.hrDocumentCategories;
	}
};

const actions = {
	getById({ rootState, rootGetters }, id) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.get(`/api/${rootState.tenantId}/hrDocuments/${id}`)
				.then(response => {
					let result = response.data;

					const loggedInEmployeeId = rootGetters['loggedInEmployee'].id;
					result = appendAgreementData(result, loggedInEmployeeId, true);
					result = appendIsRelevantToEmployee(result, loggedInEmployeeId, true);
					resolve(result);
				})
				.catch(err => {
					reject(err);
				});
		});
	},
	get({ commit, rootState }) {
		console.log('Retrieving HR Documents...');
		commit('GET_PENDING');
		return Vue.axios
			.get(`/api/${rootState.tenantId}/hrDocuments`)
			.then(response => {
				commit('GET_SUCCESS', response.data);
			})
			.catch(error => {
				commit('GET_FAILURE', error);
			});
	},
	async getDiff({ rootState }, { id, versionNo }) {
		try {
			const response = await Vue.axios.get(`/api/${rootState.tenantId}/HRDocumentsDiff/${id}/${versionNo}`);
			return success(response.data);
		} catch (err) {
			toast.error('Failed to load policy changes');
			return fail();
		}
	},
	async getPreviousVersion({ rootState }, { id, versionNo }) {
		try {
			const response = await Vue.axios.get(`/api/${rootState.tenantId}/hrDocuments/${id}/${versionNo}`);
			return success(response.data);
		} catch (err) {
			toast.error(`Failed to load policy version ${versionNo}`);
			return fail();
		}
	},
	add({ commit, dispatch, rootState }, hrDocument) {
		console.log('Adding New HR Document...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/hrDocuments`, hrDocument)
				.then(resp => {
					//commit('ADD_SUCCESS', resp.data);
					dispatch('get');
					resolve(resp.data);
				})
				.catch(err => {
					console.log('Error saving HR Document');
					console.log(err);
					reject(err);
				});
		});
	},
	edit({ commit, dispatch, rootState }, hrDocument) {
		console.log('Updating HR Document...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`/api/${rootState.tenantId}/hrDocuments/${hrDocument.id}`, hrDocument)
				.then(resp => {
					//commit('EDIT_SUCCESS', resp.data);
					dispatch('get');
					resolve(resp.data);
				})
				.catch(err => {
					toast.error('Failed to update HR Document');
					reject(err);
				});
		});
	},
	delete({ commit, rootState }, hrDocument) {
		console.log('Deleting HR Document...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.delete(`/api/${rootState.tenantId}/hrDocuments/${hrDocument.id}`)
				.then(resp => {
					commit('DELETE_SUCCESS', hrDocument.id);
					resolve(true);
				})
				.catch(err => {
					toast.error('Failed to delete HR Document');
					reject(err);
				});
		});
	},
	useTemplate({ commit, rootState, dispatch }, templateId) {
		console.log('Applying HR Document Template...');
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/hrDocuments/useTemplate/${templateId}`)
				.then(resp => {
					//commit('USE_TEMPLATE_SUCCESS', resp.data);
					dispatch('get');
					resolve(resp.data);
				})
				.catch(err => {
					toast.error('Failed to use template');
					reject(err);
				});
		});
	},
	updateToLatestTemplate({ commit, rootState, dispatch }, hrDocumentId) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`/api/${rootState.tenantId}/hrDocuments/useTemplate/${hrDocumentId}`)
				.then(resp => {
					//commit('EDIT_SUCCESS', resp.data);
					dispatch('get');
					resolve(resp.data);
				})
				.catch(err => {
					toast.error('Failed to update to latest template');
					reject(err);
				});
		});
	},
	getHrDocumentFile({ rootState }, hrDocumentId) {
		return Vue.axios({
			url: `/api/${rootState.tenantId}/HRDocuments/${hrDocumentId}/File`,
			method: 'GET',
			responseType: 'blob'
		});
	},
	getHrDocumentFileByVersion({ rootState }, { hrDocumentId, versionNo }) {
		return Vue.axios({
			url: `/api/${rootState.tenantId}/HRDocuments/${hrDocumentId}/${versionNo}/File`,
			method: 'GET',
			responseType: 'blob'
		});
	},
	uploadHrDocument({ dispatch, commit, rootState }, data) {
		console.log('Uploading HR Document...');

		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/hrDocuments/existingDocument`, data, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				})
				.then(resp => {
					commit('ADD_SUCCESS', resp.data);
					dispatch('get');
					resolve(resp.data);
				})
				.catch(err => {
					toast.error('Failed to upload HR Document');
					reject(err);
				});
		});
	},
	postAgreement({ commit, rootState, dispatch }, data) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/hrDocuments/${data.id}/agree`, data)
				.then(resp => {
					//commit('POST_AGREEMENT_SUCCESS', resp.data);
					dispatch('get');
					resolve(resp.data);
				})
				.catch(err => {
					toast.error('Failed to agree to HR Document');
					reject(err);
				});
		});
	},
	updateSettings({ commit, rootState, dispatch }, data) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`/api/${rootState.tenantId}/hrDocuments/${data.id}/settings`, data)
				.then(resp => {
					//commit('UPDATE_SETTINGS_SUCCESS', resp.data);
					dispatch('getCategoryList');
					dispatch('get');
					resolve(resp.data);
				})
				.catch(err => {
					toast.error('Failed to update settings for HR Document');
					reject(err);
				});
		});
	},
	getCategoryList({ commit, rootState }) {
		return Vue.axios
			.get(`/api/${rootState.tenantId}/hrDocuments/categories`)
			.then(response => {
				commit('GET_CATEGORY_LIST_SUCCESS', response.data.categories);
			})
			.catch(e => {
				toast.error('Failed to load HR Document Categories');
				reject(e);
			});
	},
	deleteCategory({ commit, rootState }, categoryId) {
		return Vue.axios
			.delete(`/api/${rootState.tenantId}/hrDocuments/categories/${categoryId}`)
			.then(response => {
				commit('GET_CATEGORY_LIST_SUCCESS', response.data.categories);
			})
			.catch(e => {
				toast.error('Failed to remove category');
				reject(e);
			});
	},
	addReview({ rootState, dispatch }, data) {
		return new Promise((resolve, reject) => {
			Vue.axios
				.post(`/api/${rootState.tenantId}/hrDocuments/${data.id}/review`, data)
				.then(resp => {
					dispatch('get');
					resolve(resp.data);
				})
				.catch(e => {
					const message = e.response.data.detail || 'Failed to review HR Document';
					toast.error(message);
					reject(e);
				});
		});
	},
	updatePdf({ dispatch, rootState }, { id, formData }) {
		// Replaces a PDF-backed policy and (by default) bumps version
		return new Promise((resolve, reject) => {
			Vue.axios
				.put(`/api/${rootState.tenantId}/hrDocuments/${id}/file`, formData, {
					headers: { 'Content-Type': 'multipart/form-data' }
				})
				.then(resp => {
					dispatch('get');
					resolve(resp.data);
				})
				.catch(err => {
					toast.error('Failed to replace HR Document');
					reject(err);
				});
		});
	}
};

const mutations = {
	GET_PENDING(state) {
		state.hrDocuments_loading = true;
	},
	GET_SUCCESS(state, data) {
		state.hrDocuments_loading = false;
		state.hrDocuments = data;
	},
	GET_FAILURE(state, error) {
		state.hrDocuments_loading = false;
		console.log(error);
	},
	ADD_SUCCESS(state, data) {
		//state.hrDocuments.push(data);
	},
	USE_TEMPLATE_SUCCESS(state, data) {
		state.hrDocuments.push(data);
	},
	EDIT_SUCCESS(state, data) {
		let foundIndex = state.hrDocuments.findIndex(x => x.id === data.id);
		if (foundIndex > -1) {
			state.hrDocuments.splice(foundIndex, 1, data);
		}
	},
	DELETE_SUCCESS(state, id) {
		const index = state.hrDocuments.findIndex(x => x.id === id);
		state.hrDocuments.splice(index, 1);
	},
	POST_AGREEMENT_SUCCESS(state, data) {
		let foundIndex = state.hrDocuments.findIndex(x => x.id === data.hrDocumentId);
		if (foundIndex > -1) {
			state.hrDocuments[foundIndex].agreements.push(data);
		}
	},
	UPDATE_SETTINGS_SUCCESS(state, data) {
		let foundIndex = state.hrDocuments.findIndex(x => x.id === data.id);
		if (foundIndex > -1) {
			state.hrDocuments.splice(foundIndex, 1, data);
		}
	},
	GET_CATEGORY_LIST_SUCCESS(state, data) {
		state.hrDocumentCategories = data;
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};
